import BaseElement from '@theme/base-element'

// Component styles (was src/styles/component-icon.css)
const styles = `
:host {
  box-sizing: content-box !important;
  display: inline-flex;
  height: 1em;
  width: 1em;
  font-size: var(--icon-size, inherit);
}
`
import { Task, html, nothing, unsafeCSS, unsafeHTML } from '@theme/vendor-lit'
import defer from '@theme/vendor-defer-promise'

const CACHE_BUSTER = Date.now()
// Usage
// Control the icon size by supplying relevant font-size class to HOST element
// <svg-icon
//     src='icon-close'
//     width='24px'
//     viewBox='0 0 24 24'
//   ></svg-icon>
export class SvgIcon extends BaseElement {
  static get properties() {
    return {
      src: { type: String },
    }
  }

  static loadingCartPromise = null
  static svgSpriteCache = null
  static styles = [unsafeCSS(styles)]
  static metaPathSel = `meta[data-svg-sprite-path]`

  constructor() {
    super()

    this.loadSpriteTask = new Task(this, {
      task: async () => {
        if (SvgIcon.svgSpriteCache) {
          return SvgIcon.svgSpriteCache
        }

        const spritePath = this.svgSpritePath

        if (!spritePath) {
          return nothing
        }

        if (SvgIcon.loadingCartPromise) {
          return await SvgIcon.loadingCartPromise.promise
        }
        SvgIcon.loadingCartPromise = defer()

        try {
          const response = await fetch(spritePath)
          if (!response.ok) {
            throw new Error('Network response was not ok')
          }
          const svgText = await response.text()
          // Clean up the fetched SVG text if necessary
          const parser = new DOMParser()
          const doc = parser.parseFromString(svgText, 'image/svg+xml')
          const svgSprite = doc.documentElement
          svgSprite.style.display = 'none' // Hide the sprite
          SvgIcon.svgSpriteCache = svgSprite.outerHTML

          if (SvgIcon.loadingCartPromise) {
            SvgIcon.loadingCartPromise.resolve()
            SvgIcon.loadingCartPromise = null
          }

          return SvgIcon.svgSpriteCache
        } catch (error) {
          if (SvgIcon.loadingCartPromise) {
            SvgIcon.loadingCartPromise.reject()
            SvgIcon.loadingCartPromise = null
          }
          console.error('Error fetching SVG sprite:', error)
          return error
        }
      },
    })
    this.metaEl = document.head.querySelector(SvgIcon.metaPathSel)
  }

  connectedCallback() {
    super.connectedCallback()

    if (window.Shopify?.designMode) {
      this.loadSpriteTask.run()
    }
  }

  get runningDevServer() {
    return (
      window.location.hostname === 'localhost' ||
      window.location.hostname.includes('127.0.0')
    )
  }

  get svgSpritePath() {
    if (this.runningDevServer) {
      return `/assets/icons.svg?cache=${CACHE_BUSTER}` // Dev server
    }
    return this.metaEl?.dataset.svgSpritePath
  }

  get useHref() {
    return `${this.svgSpritePath}#${this.src}`
  }

  generateSVGMarkup(icon) {
    return html`
      <svg
        class="block h-full w-full"
        aria-label=${this.ariaLabel || `Icon for ${this.src}`}
      >
        <use crossorigin="anonymous" href=${icon}></use>
      </svg>
    `
  }

  render() {
    if (this.src === undefined || this.src === null || this.src === '') {
      return html`${nothing}`
    }

    if (window.Shopify?.designMode) {
      return html`
        ${this.loadSpriteTask.render({
          complete: () => {
            if (
              !SvgIcon.svgSpriteCache ||
              typeof SvgIcon.svgSpriteCache !== 'string'
            ) {
              return nothing
            }
            return html`
              ${unsafeHTML(SvgIcon.svgSpriteCache)}
              ${this.src && this.generateSVGMarkup(`#${this.src}`)}
            `
          },
        })}
      `
    }

    return this.generateSVGMarkup(this.useHref)
  }
}

customElements.define('svg-icon', SvgIcon)
